import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ModalLayout from '../layouts/ModalLayout'
import { modalPrimaryButtonStyle } from '../styles/buttonStyles'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import TripDetailModal from './TripDetailModal'
import { calculateFuelUsedLiters, calculateTripDurationMinutes } from '../helpers/calculateConsumption';
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import GenericListFilter from '../components/GenericListFilter'
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import ExportButton from '../components/ExportButton'
import { useAuth } from '../context/AuthContext'
import {
  buildTripExportRecords,
  exportTripsCSV,
  exportTripsXLS,
  exportTripsPDF,
  exportTripsXML,
  exportTripsICS,
  exportTripsJSON
} from '../utils/tripExport'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable  
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const tripDetailFields = [
  ['tripNumber', ''],
  ['date', 'TRIPLOG.TRIPS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.TRIPS.TABLE.vehicleId'],
  ['spz', 'vehicle_license_plate'],
  ['VIN', 'TRIPLOG.TRIPS.TABLE.VIN'],
  ['driver', 'TRIPLOG.TRIPS.TABLE.driver'],
  ['startTimeIso', 'TRIPLOG.TRIPS.TABLE.startTimeIso'],
  ['startPlace', 'TRIPLOG.TRIPS.TABLE.startPlace'],
  ['endTimeIso', 'TRIPLOG.TRIPS.TABLE.endTimeIso'],
  ['endPlace', 'TRIPLOG.TRIPS.TABLE.endPlace'],
  ['odoStart', 'TRIPLOG.TRIPS.TABLE.odoStart'],
  ['odoEnd', 'TRIPLOG.TRIPS.TABLE.odoEnd'],
  ['distanceKm', 'TRIPLOG.TRIPS.TABLE.distanceKm'],
  ['duration', 'TRIPLOG.TRIPS.TABLE.durationMinutes'],
  ['purpose', 'TRIPLOG.TRIPS.TABLE.purpose'],
  ['refuel', 'TRIPLOG.TRIPS.TABLE.receiptId'],
  ['avgSpeedKmh', 'TRIPLOG.TRIPS.TABLE.avgSpeedKmh'],
  ['fuelUsed', 'TRIPLOG.TRIPS.TABLE.fuelUsedLiters'],
  ['model', 'TRIPLOG.TRIPS.TABLE.model'],
  ['typ_variant_verzia', 'TRIPLOG.TRIPS.TABLE.typ_variant_verzia'],
  ['note', 'TRIPLOG.TRIPS.TABLE.note'],
  ['mode', 'TRIPLOG.TRIPS.TABLE.mode'],
  ['gpsStart', 'TRIPLOG.TRIPS.TABLE.gpsStart'],
  ['gpsEnd', 'TRIPLOG.TRIPS.TABLE.gpsEnd']
]

const TRIP_MODAL_COLUMNS_KEY = 'tripModalColumns'
const TRIP_MODAL_COL_WIDTHS_KEY = 'tripModalColWidths'

function SortableHeaderCell({ col, orderBy, order, onSort, colWidths, onResize }) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.key })
  const customWidth = colWidths[col.key]

  return (
    <th
      ref={setNodeRef}
      style={{
        ...thStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(col.key === 'tripNumber'
  	? { width: 50, minWidth: 50, maxWidth: 50 }
  	: customWidth
  	  ? { width: customWidth, minWidth: customWidth, maxWidth: customWidth }
  	  : { minWidth: 120 }),
        position: 'relative',
        paddingRight: 8,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 6, color: '#999' }}>⠿</span>
        <TableSortLabel
          active={orderBy === col.key}
          direction={orderBy === col.key ? order : 'asc'}
          onClick={() => onSort(col.key)}
        >
          {t(col.labelKey)}
        </TableSortLabel>
      </div>
      <div style={{ position: 'absolute', right: 4, top: '20%', bottom: '20%', width: 1, background: '#9e9e9e' }} />
      <div onMouseDown={(e) => onResize(e, col.key)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize' }} />
    </th>
  )
}

const TripListModal = ({
  open,
  onClose,
  trips = [],
  filterCriteria,
  setFilterCriteria,
  openExportDropdown
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isLoggedIn = Boolean(user)
  const membershipId = user?.membership_type_id ?? null
  const limitedMembership = membershipId === 1 || membershipId === 4
  const fullMembership = !limitedMembership

  const [viewport, setViewport] = useState(window.innerWidth)
  const [modalSize, setModalSize] = useState('fullscreen')
  const [hoveredTrip, setHoveredTrip] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
  const [receipts, setReceipts] = useState([])
  const [tooltipEnabled, setTooltipEnabled] = useState(() => {
    const stored = localStorage.getItem('tooltipEnabled')
    return stored === null ? true : stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem('tooltipEnabled', tooltipEnabled)
  }, [tooltipEnabled])

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  const [selectedTrip, setSelectedTrip] = useState(null)
  const [tripDetailOpen, setTripDetailOpen] = useState(false)

const { showSearch, setShowSearch, showFilter, setShowFilter } = useSharedModalUiState()
const [searchQuery, setSearchQuery] = useState('')

const [forceOpenExport, setForceOpenExport] = useState(false)

useEffect(() => {
  if (open && openExportDropdown) {
    setForceOpenExport(true)

    setTimeout(() => {
      setForceOpenExport(false)
    }, 0)
  }
}, [open, openExportDropdown])

const [orderBy, setOrderBy] = useState(() => localStorage.getItem('tripOrderBy') || 'date')
const [order, setOrder] = useState(() => localStorage.getItem('tripOrder') || 'desc')
const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem(TRIP_MODAL_COLUMNS_KEY)
  if (stored) return JSON.parse(stored)

  return tripDetailFields.map(([key, labelKey]) => ({
    key,
    labelKey
  }))
})
const [colWidths, setColWidths] = useState(() => {
  const stored = localStorage.getItem(TRIP_MODAL_COL_WIDTHS_KEY)
  return stored ? JSON.parse(stored) : {}
})
const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => {
    localStorage.setItem(TRIP_MODAL_COLUMNS_KEY, JSON.stringify(columns))
  }, [columns])

  useEffect(() => {
    localStorage.setItem(TRIP_MODAL_COL_WIDTHS_KEY, JSON.stringify(colWidths))
  }, [colWidths])

  useEffect(() => {
    localStorage.setItem('tripOrderBy', orderBy)
  }, [orderBy])

  useEffect(() => {
    localStorage.setItem('tripOrder', order)
  }, [order])

const handleSort = (col) => {
  if (orderBy === col) {
    setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
  } else {
    setOrderBy(col)
    setOrder('asc')
  }
}

const handleMouseDown = (e, key) => {
  const startX = e.clientX
  const startWidth = colWidths[key] || 150

  const onMouseMove = (event) => {
    const delta = event.clientX - startX
    const newWidth = Math.max(80, startWidth + delta)
    setColWidths(prev => ({ ...prev, [key]: newWidth }))
  }

  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

const handleDragEnd = (event) => {
  const { active, over } = event
  if (!over || active.id === over.id) return
  const oldIndex = columns.findIndex(c => c.key === active.id)
  const newIndex = columns.findIndex(c => c.key === over.id)
  const reordered = [...columns]
  const [moved] = reordered.splice(oldIndex, 1)
  reordered.splice(newIndex, 0, moved)
  setColumns(reordered)
}

  useEffect(() => {
    const handleResize = () => setViewport(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (open) setModalSize('fullscreen')
  }, [open])

  useEffect(() => {
    const loadVehicles = async () => {
      if (!trips.length) return
      const userId = trips[0]?.userId
      if (!userId) return

      try {
        const res = await fetchWithAuth(`/users/${userId}/vehicles`)
        if (!res.ok) return
        const data = await res.json()
        if (!Array.isArray(data)) return

        const index = data.reduce((acc, vehicle) => {
          if (vehicle?.id == null) return acc
          acc[String(vehicle.id)] = vehicle
          return acc
        }, {})

        setVehiclesById(index)
      } catch {}
    }

    loadVehicles()
  }, [trips])

  const getVehicleLabel = (trip) => {
    const vehicleFromList = vehiclesById[String(trip?.vehicleId)]
    const vehicle = vehicleFromList || trip.vehicle

    return (
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      '-'
    )
  }

useEffect(() => {
  const loadReceipts = async () => {
    if (!user?.id) return

    try {
      const res = await fetchWithAuth(`/users/${user.id}/receipts`)
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      setReceipts(data)
    } catch {}
  }

  loadReceipts()
}, [user?.id])

const getTripSearchData = (trip) => {
  const vehicleFromList = vehiclesById[String(trip?.vehicleId)]
  const vehicle = vehicleFromList || trip?.vehicle

  return {
    ...trip,
    model: trip?.model ?? vehicle?.model,
    typ_variant_verzia: trip?.typ_variant_verzia ?? vehicle?.variantType,
    VIN: trip?.VIN ?? vehicle?.vin,
    vehicleName: vehicle?.name,
    vehicleModel: vehicle?.model,
    vehicleVariantType: vehicle?.variantType,
    vehicleVin: vehicle?.vin,
    vehicleSpz: vehicle?.spz,
    searchDate: trip?.date ? new Date(trip.date).toLocaleDateString('sk-SK') : '',
    searchStartTime: trip?.startTimeIso
      ? new Date(trip.startTimeIso).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
      : '',
    searchEndTime: trip?.endTimeIso
      ? new Date(trip.endTimeIso).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
      : ''
  }
}

const filteredTrips = trips.filter((trip) => {
  const itemDate = trip?.date ? trip.date.slice(0, 10) : null

  if (filterCriteria.vehicleId && String(trip.vehicleId) !== String(filterCriteria.vehicleId)) return false
  if (filterCriteria.from && itemDate < filterCriteria.from) return false
  if (filterCriteria.to && itemDate > filterCriteria.to) return false
  if (filterCriteria.month && !itemDate?.startsWith(filterCriteria.month)) return false
  if (filterCriteria.year && !itemDate?.startsWith(filterCriteria.year)) return false

  return true
})

const searchFilteredTrips = useGenericTextSearch(
  filteredTrips,
  searchQuery,
  getTripSearchData
)

const sortedTrips = [...searchFilteredTrips].sort((a, b) => {
  let aVal = a[orderBy]
  let bVal = b[orderBy]

  if (orderBy === 'tripNumber') {
    aVal = Number(a.tripNumber) || 0
    bVal = Number(b.tripNumber) || 0
  }

  if (orderBy === 'vehicleId') {
    aVal = getVehicleLabel(a)
    bVal = getVehicleLabel(b)
  }

if (orderBy === 'spz') {
  const vehicleA = vehiclesById[String(a.vehicleId)]
  const vehicleB = vehiclesById[String(b.vehicleId)]

  aVal = vehicleA?.licensePlate || vehicleA?.spz || ''
  bVal = vehicleB?.licensePlate || vehicleB?.spz || ''
}

  if (orderBy === 'date' || orderBy === 'startTimeIso' || orderBy === 'endTimeIso') {
    aVal = aVal ? new Date(aVal).getTime() : 0
    bVal = bVal ? new Date(bVal).getTime() : 0
  }

  if (orderBy === 'distanceKm') {
    aVal = Number(aVal) || 0
    bVal = Number(bVal) || 0
  }

if (orderBy === 'fuelUsed') {
  const vehicleA = vehiclesById[String(a.vehicleId)]
  const vehicleB = vehiclesById[String(b.vehicleId)]

  aVal = calculateFuelUsedLiters(a, vehicleA ? [vehicleA] : []) || 0
  bVal = calculateFuelUsedLiters(b, vehicleB ? [vehicleB] : []) || 0
}

  if (typeof aVal === 'string') aVal = aVal.toLowerCase()
  if (typeof bVal === 'string') bVal = bVal.toLowerCase()

  if (aVal > bVal) return order === 'asc' ? 1 : -1
  if (aVal < bVal) return order === 'asc' ? -1 : 1
  return 0
})

const exportRecords = buildTripExportRecords(
  sortedTrips,
  vehiclesById,
  receipts,
  calculateFuelUsedLiters,
  t
)

const exportHeaders = columns.map(c => c.key)

const selectedTripIndex = sortedTrips.findIndex(
  (item) => item?.id === selectedTrip?.id
)

const goToPrevTrip = () => {
  if (selectedTripIndex <= 0) return
  const prevTrip = sortedTrips[selectedTripIndex - 1]
  setSelectedTrip(prevTrip)
  setActiveRow(prevTrip?.id ?? null)
}

const goToNextTrip = () => {
  if (selectedTripIndex < 0 || selectedTripIndex >= sortedTrips.length - 1) return
  const nextTrip = sortedTrips[selectedTripIndex + 1]
  setSelectedTrip(nextTrip)
  setActiveRow(nextTrip?.id ?? null)
}

const formatHeaderDate = (d) => {
  if (!d) return ''
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return d

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

let periodText = t('TRIPLOG.STATISTICS.ALL_TIME')

if (filterCriteria?.month) {
  const [year, month] = filterCriteria.month.split('-')
  periodText = `${month}-${year}`
} else if (filterCriteria?.year) {
  periodText = filterCriteria.year
} else if (filterCriteria?.from || filterCriteria?.to) {
  const from = formatHeaderDate(filterCriteria.from)
  const to = formatHeaderDate(filterCriteria.to)
  periodText = `${from} - ${to}`
}

let vehicleText = t('TRIPLOG.BUTTON.ALL_VEHICLES')

if (filterCriteria?.vehicleId) {
  const v = vehiclesById[String(filterCriteria.vehicleId)]

  if (v) {
    const name = v.name || v.model || ''
    const plate = v.licensePlate || v.spz || ''

    vehicleText = plate ? `${name} (${plate})` : name
  }
}

const odometerHeaderValues = (() => {
  const isSingleVehicleFiltered = Boolean(filterCriteria?.vehicleId)
  if (!isSingleVehicleFiltered || filteredTrips.length === 0) {
    return {
      isSingleVehicleFiltered,
      odoStart: '',
      odoEnd: ''
    }
  }

  const getTripTime = (trip) => {
    const withTime = trip?.startTimeIso ? new Date(trip.startTimeIso).getTime() : NaN
    if (!Number.isNaN(withTime)) return withTime

    const dateOnly = trip?.date ? new Date(trip.date).getTime() : NaN
    if (!Number.isNaN(dateOnly)) return dateOnly

    return 0
  }

  const chronologicallySortedTrips = [...filteredTrips].sort((a, b) => {
    const timeDiff = getTripTime(a) - getTripTime(b)
    if (timeDiff !== 0) return timeDiff

    const tripNumberA = Number(a?.tripNumber) || 0
    const tripNumberB = Number(b?.tripNumber) || 0
    if (tripNumberA !== tripNumberB) return tripNumberA - tripNumberB

    const idA = Number(a?.id) || 0
    const idB = Number(b?.id) || 0
    return idA - idB
  })

  const firstTrip = chronologicallySortedTrips[0]
  const lastTrip = chronologicallySortedTrips[chronologicallySortedTrips.length - 1]

  return {
    isSingleVehicleFiltered,
    odoStart: firstTrip?.odoStart != null ? Number(firstTrip.odoStart) : '',
    odoEnd: lastTrip?.odoEnd != null ? Number(lastTrip.odoEnd) : ''
  }
})()

  if (!open) return null

  return (
    <ModalLayout open={open} onClose={onClose} size={modalSize} closeOnEscape={!tripDetailOpen}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

        <div
          style={{
            position: 'absolute',
            top: -10,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >

<GenericSearchToggle
  visible={showSearch}
  query={searchQuery}
  onQueryChange={setSearchQuery}
  onToggle={() => {
    setShowSearch(prev => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }}
  placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
/>

<FilterToggleButton
  visible={showFilter}
  active={
    Object.values(filterCriteria || {}).some(v => v && v !== '')
  }
  onToggle={() => setShowFilter(prev => !prev)}
/>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
              fontSize: 13,
              userSelect: 'none',
	      marginTop: 5
            }}
          >
            <input
              type="checkbox"
              checked={tooltipEnabled}
              onChange={(e) => setTooltipEnabled(e.target.checked)}
              style={{
                cursor: 'pointer',
                appearance: 'none',
                width: 16,
                height: 16,
                border: '1px solid #1e3f44',
                borderRadius: 3,
                background: tooltipEnabled ? '#06857a' : '#fff'
              }}
            />
            {t('TRIPLOG.SERVICECOSTS.TOGGLE')}
          </label>
<ResetTableButton
isModified={
  Object.keys(colWidths).length > 0 ||
  JSON.stringify(columns.map(c => c.key)) !==
    JSON.stringify(tripDetailFields.map(([key]) => key)) ||
  orderBy !== 'date' ||
  order !== 'desc'
}
  onReset={() => {
const resetCols = tripDetailFields.map(([key, labelKey]) => ({
  key,
  labelKey
}))

    setColumns(resetCols)
    setColWidths({})
setOrderBy('date')
setOrder('desc')

    localStorage.removeItem(TRIP_MODAL_COLUMNS_KEY)
    localStorage.removeItem(TRIP_MODAL_COL_WIDTHS_KEY)
    localStorage.removeItem('tripOrderBy')
    localStorage.removeItem('tripOrder')
  }}
/>

<ExportButton
  forceOpen={forceOpenExport}
  onCSV={()=>exportTripsCSV(exportRecords, exportHeaders, t, periodText, vehicleText, odometerHeaderValues)}
  onXLS={()=>exportTripsXLS(exportRecords, exportHeaders, t, periodText, vehicleText, odometerHeaderValues)}
  onPDF={()=>exportTripsPDF(exportRecords, exportHeaders, t, periodText, vehicleText, odometerHeaderValues)}
  onXML={()=>exportTripsXML(exportRecords, exportHeaders)}
  onICS={()=>exportTripsICS(exportRecords)}
  onJSON={()=>exportTripsJSON(exportRecords)}
  isLoggedIn={isLoggedIn}
  canUsePremiumFormats={isLoggedIn && fullMembership}
/>
          <button
            onClick={() =>
              setModalSize(prev => prev === 'fullscreen' ? 'max' : 'fullscreen')
            }
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 32
            }}
          >
            ⛶
          </button>
        </div>

        <h3 style={{ fontSize: 20, marginTop: 0, marginBottom: 10 }}>
          {t('TRIPLOG.TAB.TRIPS')}
        </h3>

{showFilter && (
  <GenericListFilter
    value={filterCriteria}
    onChange={setFilterCriteria}
vehicles={Object.values(vehiclesById).map(v => {
  const name = v.name || v.model || v.licensePlate || v.spz || `Vozidlo #${v.id}`
  const plate = v.licensePlate || v.spz || ''

  return {
    value: String(v.id),
    label: plate && name !== plate ? `${name} (${plate})` : name
  }
})}
    labels={{
      allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
      from: t('TRIPLOG.FILTER_FROM'),
      to: t('TRIPLOG.FILTER_TO'),
      month: t('TRIPLOG.FILTER_MONTH'),
      year: t('TRIPLOG.FILTER_YEAR'),
      clear: t('TRIPLOG.BUTTON.CLEAR')
    }}
  />
)}

<div
  style={{
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    minHeight: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }}
>
<div
  style={{
    overflow: 'auto',
    flex: 1,
    minHeight: 0
  }}
>
<table
  style={{
    borderCollapse: 'collapse',
    width: 'max-content',
    minWidth: '100%',
    fontSize: 12,
    whiteSpace: 'nowrap'
  }}
>
<thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
  <tr style={{ fontWeight: 700 }}>
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={columns.map(c => c.key)} strategy={horizontalListSortingStrategy}>
        {columns.map((col) => (
          <SortableHeaderCell
            key={col.key}
            col={col}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            colWidths={colWidths}
            onResize={handleMouseDown}
          />
        ))}
      </SortableContext>
    </DndContext>
  </tr>
</thead>

            <tbody>
              {sortedTrips.length === 0 ? (
                <tr>
                  <td colSpan={tripDetailFields.length} style={{ padding: 16 }}>
                  </td>
                </tr>
              ) : (
                sortedTrips.map(trip => (
		<tr
		  key={trip.id}
style={{
  borderTop: '1px solid #ececec',
  borderLeft: !trip?.gpsStart && !trip?.gpsEnd
    ? '3px solid #06857a'
    : '3px solid transparent',

  backgroundColor:
    activeRow === trip.id
      ? '#e6f4f1'
      : hoveredTrip?.id === trip.id
        ? '#f3fafa'
        : (!trip?.gpsStart && !trip?.gpsEnd)
          ? '#f6ffff'
          : '#fff',

  cursor: 'pointer'
}}
  		onClick={() => {
 		   setActiveRow(trip.id)
  		  setSelectedTrip(trip)
 		   setTripDetailOpen(true)
 		 }}
 		 onMouseEnter={(e) => {
 		   if (e.target.closest('.trip-detail-button')) return
 		   setHoveredTrip(trip)
 		 }}
 		 onMouseMove={(e) => {
   		 if (e.target.closest('.trip-detail-button')) {
 		     setHoveredTrip(null)
  		  } else {
   		   setHoveredTrip(trip)
  		  }
 		 }}
 		 onMouseLeave={(e) => {
   		 const related = e.relatedTarget
 		   if (related && related.closest && related.closest('.vehicle-tooltip')) return
  		  setHoveredTrip(null)
 		 }}
		>
                    {columns.map(({ key }) => (
                      <td key={key} style={tdStyle}>
{key === 'tripNumber'
  ? trip.tripNumber

: key === 'vehicleId'
  ? getVehicleLabel(trip)

: key === 'spz'
  ? (() => {
      const vehicle = vehiclesById[String(trip.vehicleId)]
      return vehicle?.licensePlate || vehicle?.spz || '-'
    })()

: key === 'model'
  ? (trip.model ?? vehiclesById[String(trip.vehicleId)]?.model ?? '-')

: key === 'typ_variant_verzia'
  ? (trip.typ_variant_verzia ?? vehiclesById[String(trip.vehicleId)]?.variantType ?? '-')

: key === 'VIN'
  ? (trip.VIN ?? vehiclesById[String(trip.vehicleId)]?.vin ?? '-')

: key === 'distanceKm'
  ? (trip.distanceKm != null && !isNaN(Number(trip.distanceKm))
      ? Number(trip.distanceKm).toFixed(1)
      : '-')

: key === 'avgSpeedKmh'
  ? (typeof trip.avgSpeedKmh === 'number' && trip.avgSpeedKmh > 0
      ? Number(trip.avgSpeedKmh).toFixed(1)
      : '-')

: key === 'fuelUsed'
  ? (() => {
      console.log('distanceKm →', typeof trip.distanceKm, trip.distanceKm)

      const vehicle = vehiclesById[String(trip.vehicleId)]
      const liters = calculateFuelUsedLiters(
        trip,
        vehicle ? [vehicle] : []
      )
      return liters != null ? liters.toFixed(2) : '-'
    })()

: key === 'refuel'
  ? (() => {
      const id = trip?.receiptId ?? trip?.receipt_id
      if (!id) return '-'

      const receipt = receipts.find(r => String(r.id) === String(id))
      if (!receipt) return '-'

      return `${receipt.units ?? '-'} ${receipt.unit ?? ''}`
    })()

  : key === 'date'
    ? (trip.date
        ? new Date(trip.date).toLocaleDateString('sk-SK')
        : '-')

  : key === 'startTimeIso' || key === 'endTimeIso'
    ? (trip[key]
        ? new Date(trip[key]).toLocaleTimeString('sk-SK', {
            hour: '2-digit',
            minute: '2-digit'
          })
        : '-')

: key === 'duration'
  ? (() => {
      const minutes = calculateTripDurationMinutes(
        trip.startTimeIso,
        trip.endTimeIso
      )

      if (minutes == null) return '-'

      const h = String(Math.floor(minutes / 60)).padStart(2, '0')
      const m = String(minutes % 60).padStart(2, '0')

      return `${h}:${m}`
    })()

: key === 'mode'
  ? t(`TRIPLOG.MODES.${(trip.mode || '').toUpperCase()}`)
  : typeof trip[key] === 'object' && trip[key] !== null
    ? `${trip[key].latitude}, ${trip[key].longitude}`
    : trip[key] ?? '-'}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
 </div>
        {tooltipEnabled && hoveredTrip && (
          <div
            className="vehicle-tooltip"
            onMouseLeave={() => setHoveredTrip(null)}
            style={{
              position: 'fixed',
              right:
                viewport >= 2400 ? 69 :
                viewport >= 1920 ? 51 :
                viewport >= 1600 ? 40 :
                viewport >= 1366 ? 38 : 20,
              bottom:
                viewport >= 1920 ? 120 :
                viewport >= 1600 ? 110 :
                viewport >= 1366 ? 105 : 30,
              fontSize:
                viewport >= 1920 ? 14 :
                viewport >= 1600 ? 14 :
                viewport >= 1366 ? 13 : 13,
              maxHeight:
                viewport >= 1920 ? '80vh' :
                viewport >= 1600 ? '75vh' : '96vh',
              background: '#f3fafa',
              border: '1px solid #1e3f44',
              borderRadius: 8,
              padding: '16px 20px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
              zIndex: 9999,
              overflowY: 'auto'
            }}
          >
{tripDetailFields
  .filter(([key]) => key !== 'tripNumber')
  .map(([key, labelKey]) => (
  <div key={key} style={{ marginBottom: 6, display: 'flex' }}>
    <strong style={{ minWidth: 200 }}>
      {t(labelKey)}:
    </strong>
    <span style={{ color: '#1e3f44', fontWeight: 600 }}>
      {key === 'vehicleId'
        ? getVehicleLabel(hoveredTrip)

: key === 'spz'
  ? (() => {
      const vehicle = vehiclesById[String(hoveredTrip.vehicleId)]
      return vehicle?.licensePlate || vehicle?.spz || '-'
    })()

: key === 'model'
  ? (hoveredTrip.model ?? vehiclesById[String(hoveredTrip.vehicleId)]?.model ?? '-')

: key === 'typ_variant_verzia'
  ? (hoveredTrip.typ_variant_verzia ?? vehiclesById[String(hoveredTrip.vehicleId)]?.variantType ?? '-')

: key === 'VIN'
  ? (hoveredTrip.VIN ?? vehiclesById[String(hoveredTrip.vehicleId)]?.vin ?? '-')

: key === 'distanceKm'
  ? (hoveredTrip.distanceKm != null && !isNaN(Number(hoveredTrip.distanceKm))
      ? Number(hoveredTrip.distanceKm).toFixed(1)
      : '-')

        : key === 'avgSpeedKmh'
          ? (typeof hoveredTrip.avgSpeedKmh === 'number' && hoveredTrip.avgSpeedKmh > 0
              ? Number(hoveredTrip.avgSpeedKmh).toFixed(1)
              : '-')

        : key === 'fuelUsed'
          ? (() => {
              const vehicle = vehiclesById[String(hoveredTrip.vehicleId)]
              const liters = calculateFuelUsedLiters(
                hoveredTrip,
                vehicle ? [vehicle] : []
              )
              return liters != null ? liters.toFixed(2) : '-'
            })()

: key === 'refuel'
  ? (() => {
      const id = hoveredTrip?.receiptId ?? hoveredTrip?.receipt_id
      if (!id) return '-'

      const receipt = receipts.find(r => String(r.id) === String(id))
      if (!receipt) return '-'

      return `${receipt.units ?? '-'} ${receipt.unit ?? ''} | ${receipt.total_price ?? '-'} ${receipt.currency ?? ''}`
    })()

        : key === 'date'
          ? (hoveredTrip.date
              ? new Date(hoveredTrip.date).toLocaleDateString('sk-SK')
              : '-')

: key === 'startTimeIso' || key === 'endTimeIso'
  ? (hoveredTrip[key]
      ? new Date(hoveredTrip[key]).toLocaleString('sk-SK')
      : '-')

: key === 'duration'
  ? (() => {
      const minutes = calculateTripDurationMinutes(
        hoveredTrip.startTimeIso,
        hoveredTrip.endTimeIso
      )

      if (minutes == null) return '-'

      const h = String(Math.floor(minutes / 60)).padStart(2, '0')
      const m = String(minutes % 60).padStart(2, '0')

      return `${h}:${m}`
    })()

: key === 'mode'
  ? t(`TRIPLOG.MODES.${(hoveredTrip.mode || '').toUpperCase()}`)
  : typeof hoveredTrip[key] === 'object' && hoveredTrip[key] !== null
    ? `${hoveredTrip[key].latitude}, ${hoveredTrip[key].longitude}`
    : hoveredTrip[key] ?? '-'}
    </span>
  </div>
))}
          </div>
        )}

        <div style={{ marginTop: 15, display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={onClose}
            onMouseEnter={() => setHoverBtn('close')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('close')}
            onMouseUp={() => setPressedBtn(null)}
            style={modalPrimaryButtonStyle(
              pressedBtn,
              'close',
              hoverBtn === 'close'
            )}
          >
            {t('common.close')}
          </button>
        </div>

      </div>

      <TripDetailModal
        open={tripDetailOpen}
        onClose={() => {
          setTripDetailOpen(false)
          setSelectedTrip(null)
    setActiveRow(null)
        }}
        trip={selectedTrip}
        onPrevTrip={goToPrevTrip}
        onNextTrip={goToNextTrip}
        hasPrevTrip={selectedTripIndex > 0}
        hasNextTrip={selectedTripIndex >= 0 && selectedTripIndex < sortedTrips.length - 1}
      />
    </ModalLayout>
  )
}

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #c7c7c7',
  top: 0,
  background: '#c8e1e1',
  zIndex: 20
}

const tdStyle = {
  padding: '8px 12px'
}

export default TripListModal